/**
 * 密码加密工具 - 使用 RSA 公钥加密
 * 密码在传输前会被加密，确保安全性
 * 公钥从服务端动态获取
 */

import { httpGet } from '@/services/http';

// 缓存公钥
let cachedPublicKey: string | null = null;

/**
 * 从服务端获取 RSA 公钥
 * @returns PEM 格式的公钥
 */
export async function fetchPublicKey(): Promise<string> {
  if (cachedPublicKey) {
    return cachedPublicKey;
  }

  try {
    const response = await httpGet<{ public_key: string }>('/auth/public-key');
    cachedPublicKey = response.public_key;
    return cachedPublicKey;
  } catch (error) {
    console.error('获取公钥失败:', error);
    throw new Error('获取加密公钥失败');
  }
}

/**
 * 清除缓存的公钥（用于密钥更新后）
 */
export function clearCachedPublicKey(): void {
  cachedPublicKey = null;
}

/**
 * 使用 Web Crypto API 加密密码
 * @param password 明文密码
 * @param publicKeyPEM PEM 格式的公钥（可选，如果不提供则自动获取）
 * @returns Base64 编码的加密密码
 */
export async function encryptPassword(password: string, publicKeyPEM?: string): Promise<string> {
  try {
    // 获取公钥
    const publicKeyContent = publicKeyPEM || await fetchPublicKey();
    
    // 将 PEM 格式的公钥转换为 ArrayBuffer
    const pemContents = publicKeyContent.replace(/-----.*?-----/g, '').replace(/\s/g, '');
    const binaryDerString = atob(pemContents);
    const binaryDer = new Uint8Array(binaryDerString.length);
    for (let i = 0; i < binaryDerString.length; i++) {
      binaryDer[i] = binaryDerString.charCodeAt(i);
    }

    // 导入公钥
    const publicKey = await crypto.subtle.importKey(
      'spki',
      binaryDer.buffer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      false,
      ['encrypt']
    );

    // 编码密码为 UTF-8
    const encoder = new TextEncoder();
    const passwordBuffer = encoder.encode(password);

    // 使用 RSA-OAEP 加密
    const encryptedBuffer = await crypto.subtle.encrypt(
      {
        name: 'RSA-OAEP',
      },
      publicKey,
      passwordBuffer
    );

    // 转换为 Base64
    const encryptedArray = new Uint8Array(encryptedBuffer);
    let binary = '';
    for (let i = 0; i < encryptedArray.length; i++) {
      binary += String.fromCharCode(encryptedArray[i]);
    }
    return btoa(binary);
  } catch (error) {
    console.error('密码加密失败:', error);
    throw new Error('密码加密失败');
  }
}

/**
 * 测试加密功能
 */
export async function testEncryption(): Promise<void> {
  try {
    const encrypted = await encryptPassword('Test@123');
    console.log('加密测试成功:', encrypted.substring(0, 50) + '...');
  } catch (error) {
    console.error('加密测试失败:', error);
  }
}
